import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, MessageSquare, Star, Search, Tag, TrendingUp, ChevronRight, Crown, Lock } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ProPaywall from '../components/ProPaywall';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const MACRO_TAGS = ['Rates', 'DXY', 'Liquidity', 'Equities', 'Crypto', 'Commodities', 'Volatility'];

const MacroBriefList = () => {
  const { isPro } = useAuth();
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchBriefs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTag]);

  const fetchBriefs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedTag) params.append('tag', selectedTag);
      const response = await axios.get(`${API}/macro-briefs?${params}`);
      setBriefs(response.data);
    } catch (error) {
      console.error('Error fetching briefs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatWeekRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  const filteredBriefs = briefs.filter(brief =>
    brief.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    brief.summary.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex items-center gap-2 bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-full px-4 py-2 mb-4">
              <TrendingUp className="text-[#C5A059]" size={16} />
              <span className="text-[#C5A059] font-semibold text-sm uppercase tracking-wider">Institutional Research</span>
            </div>
            <h1 className="font-manrope font-bold text-4xl sm:text-5xl text-white mb-4" data-testid="macro-brief-title">
              Weekly <span className="text-[#C5A059]">Macro Brief</span>
            </h1>
            <p className="text-gray-300 text-lg max-w-2xl">
              Institutional-grade macro analysis and market intelligence. Updated weekly with actionable insights for serious investors.
            </p>
          </div>

          {/* Filters */}
          <div className="mb-8 flex flex-col md:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                type="text"
                placeholder="Search briefs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[#111111] border-white/10 text-white placeholder:text-gray-500"
                data-testid="search-briefs"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedTag === '' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedTag('')}
                className={selectedTag === '' ? 'bg-[#C5A059] text-black' : 'border-white/20 text-gray-300'}
              >
                All
              </Button>
              {MACRO_TAGS.map(tag => (
                <Button
                  key={tag}
                  variant={selectedTag === tag ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedTag(tag)}
                  className={selectedTag === tag ? 'bg-[#007AFF] text-white' : 'border-white/20 text-gray-300'}
                >
                  {tag}
                </Button>
              ))}
            </div>
          </div>

          {/* Briefs Grid */}
          {!isPro ? (
            <div>
              {/* Show a teaser of briefs with overlay */}
              {!loading && filteredBriefs.length > 0 && (
                <div className="relative mb-8">
                  <div className="opacity-40 pointer-events-none">
                    {filteredBriefs.slice(0, 2).map((brief, index) => (
                      <div
                        key={brief.brief_id}
                        className={`block bg-gradient-to-br from-[#111111] to-[#0A0A0A] border rounded-lg overflow-hidden mb-6 ${
                          brief.featured ? 'border-[#C5A059]/30' : 'border-white/10'
                        }`}
                      >
                        <div className="p-6 md:p-8">
                          <div className="flex items-center gap-2 mb-3">
                            <Lock size={14} className="text-[#C5A059]" />
                            <span className="text-xs text-[#C5A059] font-semibold uppercase">Pro Content</span>
                          </div>
                          <h2 className="font-manrope font-bold text-xl md:text-2xl text-white mb-3">{brief.title}</h2>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400 mb-4">
                            <div className="flex items-center gap-1">
                              <Calendar size={14} />
                              <span>{formatWeekRange(brief.week_start_date, brief.week_end_date)}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock size={14} />
                              <span>{brief.read_time_minutes} min read</span>
                            </div>
                          </div>
                          <p className="text-gray-300 text-sm line-clamp-2">{brief.summary}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0A0A]/80 to-[#0A0A0A]" />
                </div>
              )}
              <ProPaywall feature="Weekly Macro Briefs" />
            </div>
          ) : loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#C5A059]"></div>
            </div>
          ) : filteredBriefs.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400">No macro briefs found.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredBriefs.map((brief, index) => (
                <Link
                  key={brief.brief_id}
                  to={`/macro-brief/${brief.slug}`}
                  className={`block bg-gradient-to-br from-[#111111] to-[#0A0A0A] border rounded-lg overflow-hidden hover:border-[#C5A059]/50 transition-all group ${
                    brief.featured ? 'border-[#C5A059]/30' : 'border-white/10'
                  }`}
                  data-testid={`brief-card-${index}`}
                >
                  <div className="p-6 md:p-8">
                    <div className="flex flex-col lg:flex-row gap-6">
                      {/* Cover Image */}
                      {brief.cover_image && (
                        <div className="lg:w-64 h-40 lg:h-auto rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={brief.cover_image}
                            alt={brief.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      )}

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* Badge */}
                        {brief.featured && (
                          <div className="inline-flex items-center gap-1 bg-[#C5A059]/20 text-[#C5A059] px-3 py-1 rounded-full text-xs font-semibold mb-3">
                            <Star size={12} />
                            Featured
                          </div>
                        )}

                        {/* Title */}
                        <h2 className="font-manrope font-bold text-xl md:text-2xl text-white group-hover:text-[#C5A059] transition-colors mb-3 line-clamp-2">
                          {brief.title}
                        </h2>

                        {/* Meta */}
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400 mb-4">
                          <div className="flex items-center gap-1">
                            <Calendar size={14} />
                            <span>{formatWeekRange(brief.week_start_date, brief.week_end_date)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock size={14} />
                            <span>{brief.read_time_minutes} min read</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <MessageSquare size={14} />
                            <span>{brief.comment_count} comments</span>
                          </div>
                        </div>

                        {/* Summary */}
                        <p className="text-gray-300 text-sm mb-4 line-clamp-2">
                          {brief.summary}
                        </p>

                        {/* Highlights */}
                        {brief.highlights && brief.highlights.length > 0 && (
                          <ul className="space-y-1 mb-4">
                            {brief.highlights.slice(0, 3).map((highlight, i) => (
                              <li key={i} className="flex items-start gap-2 text-gray-400 text-sm">
                                <span className="text-[#C5A059] mt-1">•</span>
                                <span className="line-clamp-1">{highlight}</span>
                              </li>
                            ))}
                          </ul>
                        )}

                        {/* Tags */}
                        <div className="flex flex-wrap gap-2">
                          {brief.tags.slice(0, 5).map(tag => (
                            <span
                              key={tag}
                              className="text-xs px-2 py-1 bg-[#007AFF]/10 text-[#007AFF] border border-[#007AFF]/20 rounded"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Arrow */}
                      <div className="hidden lg:flex items-center">
                        <ChevronRight className="text-gray-500 group-hover:text-[#C5A059] transition-colors" size={24} />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MacroBriefList;
